// src/tabs/SousLocationView.jsx — vue financière sous-location pour un log
// parent en p6 (Chambre Zen).
//
// Affiche : KPIs (Recettes / Charges / Bénéfice) + 2 listes éditables de
// lignes (loyer mensuel, charges variables). Le CA `resas` est passé en prop
// par le parent (caller agrège les résas des chambres lc1/lc2/lc3).
//
// Utilisé par App.jsx (vue Données) — sera repassé à terme dans ComptaTab si
// besoin.

import { useState } from "react"
import { DS } from "../lib/ui"
import { fmtE, uid } from "../lib/format"
import { getSousLocData } from "../lib/calc"
import { canEditMonth, addMonthsMk } from "../lib/resa-invoice-month"
import { Modal } from "../components/Modal"
import LoyerLineModal from "../modals/LoyerLineModal"

// Deep-clone des lignes loyer M-1 vers M avec de NOUVEAUX ids (uid()).
// Anti-collision cross-mois (ADR-051) : un id réutilisé entre 2 buckets de
// mois est un risque, on régénère systématiquement. label + montant conservés.
// Pure : ne mute pas srcLoyer. Entrée non-array → [].
export function cloneLoyerLinesForReport(srcLoyer) {
  if (!Array.isArray(srcLoyer)) return []
  return srcLoyer.map(l => ({ id: uid(), label: l.label, montant: l.montant }))
}

export default function SousLocationView({ log, month, data, saveData, resas }) {
  const [loyerModal, setLoyerModal] = useState(null)
  const [chargeModal, setChargeModal] = useState(null)
  const [confirmDel, setConfirmDel] = useState(null)
  const [reportConfirm, setReportConfirm] = useState(null)
  const slData = getSousLocData(data, month, log.id)
  const loyer = slData.loyer
  const chargesVar = slData.chargesVar
  const totalLoyer = loyer.reduce((s, l) => s + (l.montant || 0), 0)
  const totalChargesVar = chargesVar.reduce((s, c) => s + (c.montant || 0), 0)
  const totalCharges = totalLoyer + totalChargesVar
  const ca = (resas || []).filter(Boolean).reduce((s, r) => s + (r.montant || 0), 0)
  const benefice = ca - totalCharges
  const editable = canEditMonth(month)

  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 L'ENREGISTREMENT N'ÉTAIT PAS ATTENDU — corrigé le 2026-08-05
  // ═══════════════════════════════════════════════════════════════════════
  // `saveData(nd)` partait sans `await` et son échec était avalé plus haut
  // (App.jsx). Résultat : la modale se fermait, la ligne s'affichait, tout
  // paraissait enregistré — et au rechargement suivant, le loyer ou la charge
  // avait disparu, sans le moindre message. Sur la sous-location, ce sont des
  // montants qui entrent directement dans le calcul du bénéfice : une perte
  // comptable silencieuse.
  //
  // `updateSL` attend désormais le résultat et renvoie true/false. Les
  // appelants ne referment leur modale QUE si l'écriture a réussi — sinon
  // l'utilisateur voit l'erreur et garde sa saisie sous les yeux pour
  // réessayer.
  const updateSL = async (newLoyer, newChargesVar) => {
    // Garde plancher : aucune écriture sur un mois facturé/verrouillé (≤ mars
    // 2026). Avril+ reste éditable pour corrections (cf. canEditMonth + édition
    // résa App.jsx). Centralisé ici = couvre saveLoyerLine/deleteLoyerLine/
    // saveChargeVar/deleteChargeVar/report (chemin d'écriture commun).
    if (!canEditMonth(month)) { alert("Mois verrouillé (factures émises)"); return false }
    const nd = JSON.parse(JSON.stringify(data))
    if (!nd[month]) nd[month] = {}
    if (!nd[month][log.id]) nd[month][log.id] = { reservations: [], extras: [], loyer: [], chargesVar: [] }
    nd[month][log.id].loyer = newLoyer
    nd[month][log.id].chargesVar = newChargesVar
    const ok = await saveData(nd)
    if (ok === false) {
      alert(
        "Échec de l'enregistrement — le loyer et les charges n'ont PAS été sauvegardés.\n\n"
        + "Vérifiez votre connexion et réessayez. Ne quittez pas cet écran : votre saisie serait perdue.",
      )
      return false
    }
    return true
  }

  // Copie les lignes loyer M-1 → M (loyer SEUL, chargesVar NON reporté).
  // N'écrit QUE le mois courant ; M-1 reste strictement intact.
  const doReport = async (prevMk) => {
    const srcLoyer = data?.[prevMk]?.[log.id]?.loyer
    // On ne ferme la confirmation que si le report est réellement enregistré.
    if (await updateSL(cloneLoyerLinesForReport(srcLoyer), chargesVar)) {
      setReportConfirm(null)
    }
  }

  const handleReport = () => {
    if (!canEditMonth(month)) { alert("Mois verrouillé (factures émises)"); return }
    const prevMk = addMonthsMk(month, -1)
    // Lecture en data BRUT (PAS getSousLocData → éviter le template-à-0).
    const srcLoyer = data?.[prevMk]?.[log.id]?.loyer
    if (!Array.isArray(srcLoyer) || srcLoyer.length === 0) {
      alert(`Aucune charge à reporter depuis ${prevMk}`); return
    }
    const curLoyer = data?.[month]?.[log.id]?.loyer
    if (Array.isArray(curLoyer) && curLoyer.length > 0) {
      // M déjà rempli → confirmation chiffrée avant remplacement.
      setReportConfirm({
        prevMk,
        prevTotal: srcLoyer.reduce((s, l) => s + (l.montant || 0), 0),
        prevCount: srcLoyer.length,
        curTotal: curLoyer.reduce((s, l) => s + (l.montant || 0), 0),
        curCount: curLoyer.length,
      })
      return
    }
    // M vide → report direct.
    doReport(prevMk)
  }

  // Dans les 4 handlers : la modale ne se referme QUE si l'écriture a réussi.
  // Auparavant elle se fermait toujours, ce qui faisait passer un échec pour
  // un succès.
  const saveLoyerLine = async (line) => {
    const idx = loyer.findIndex(l => l.id === line.id)
    const newLoyer = idx >= 0 ? loyer.map((l, i) => i === idx ? line : l) : [...loyer, line]
    if (await updateSL(newLoyer, chargesVar)) setLoyerModal(null)
  }
  const deleteLoyerLine = async (id) => {
    if (await updateSL(loyer.filter(l => l.id !== id), chargesVar)) setConfirmDel(null)
  }
  const saveChargeVar = async (charge) => {
    const idx = chargesVar.findIndex(c => c.id === charge.id)
    const newCV = idx >= 0 ? chargesVar.map((c, i) => i === idx ? charge : c) : [...chargesVar, charge]
    if (await updateSL(loyer, newCV)) setChargeModal(null)
  }
  const deleteChargeVar = async (id) => {
    if (await updateSL(loyer, chargesVar.filter(c => c.id !== id))) setConfirmDel(null)
  }

  return (
    <div style={{padding:"14px 16px",fontFamily:DS.fontBody}}>
      {confirmDel?.type === "loyer" && <Modal title="Supprimer cette ligne ?" msg="Action irréversible." onOk={() => deleteLoyerLine(confirmDel.id)} onCancel={() => setConfirmDel(null)}/>}
      {confirmDel?.type === "charge" && <Modal title="Supprimer cette charge ?" msg="Action irréversible." onOk={() => deleteChargeVar(confirmDel.id)} onCancel={() => setConfirmDel(null)}/>}
      {loyerModal && <LoyerLineModal line={loyerModal === "new" ? null : loyerModal} onSave={saveLoyerLine} onClose={() => setLoyerModal(null)}/>}
      {chargeModal && <LoyerLineModal line={chargeModal === "new" ? null : chargeModal} onSave={saveChargeVar} onClose={() => setChargeModal(null)}/>}
      {reportConfirm && <Modal
        title="Remplacer le loyer du mois ?"
        msg={`Le loyer de ce mois (${fmtE(reportConfirm.curTotal)}, ${reportConfirm.curCount} ligne(s)) sera REMPLACÉ par celui de ${reportConfirm.prevMk} (${fmtE(reportConfirm.prevTotal)}, ${reportConfirm.prevCount} ligne(s)). Action irréversible.`}
        okLabel="Remplacer"
        onOk={() => doReport(reportConfirm.prevMk)}
        onCancel={() => setReportConfirm(null)}/>}

      <div className="fbz-grid-3" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
        {[["Recettes", fmtE(ca), "#f0f4f8", DS.navy], ["Charges", fmtE(totalCharges), "#fff8f0", DS.charge], ["Bénéfice", fmtE(benefice), benefice >= 0 ? "#f0faf2" : "#fdf2f2", benefice >= 0 ? DS.skyDeep : DS.red]].map(([label, val, bg, color]) => (
          <div key={label} style={{textAlign:"center",background:bg,borderRadius:10,padding:"10px 8px"}}>
            <div style={{fontSize:10,color:"#888",fontWeight:600,textTransform:"uppercase",letterSpacing:.4}}>{label}</div>
            <div style={{fontFamily:DS.fontDisplay,fontSize:14,fontWeight:700,color,marginTop:3}}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{background:"#f8fafd",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div>
            <div style={{fontFamily:DS.fontDisplay,fontSize:13,fontWeight:700,color:DS.navy}}>Loyer du mois</div>
            <div style={{fontSize:11,color:"#888",marginTop:1}}>Total : {fmtE(totalLoyer)}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <button onClick={handleReport} disabled={!editable} title={editable ? "Copier le loyer du mois précédent" : "Mois verrouillé (factures émises)"} style={{background:"#fff",color:editable?DS.navy:"#bbb",border:`1px solid ${editable?DS.navy:"#ddd"}`,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:editable?"pointer":"not-allowed",fontFamily:DS.fontBody}}>⟳ Reporter le mois précédent</button>
            <button onClick={() => setLoyerModal("new")} style={{background:DS.navy,color:"#fff",border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}>+ Ligne</button>
          </div>
        </div>
        {loyer.map(line => (
          <div key={line.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #eef2f7"}}>
            <span style={{fontSize:12,color:"#444"}}>{line.label}</span>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontFamily:DS.fontDisplay,fontSize:13,fontWeight:700,color:DS.charge}}>{fmtE(line.montant)}</span>
              <button onClick={() => setLoyerModal(line)} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}}>✏️</button>
              <button onClick={() => setConfirmDel({ type: "loyer", id: line.id })} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}}>🗑️</button>
            </div>
          </div>
        ))}
      </div>

      <div style={{background:"#f8fafd",borderRadius:10,padding:"12px 14px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div>
            <div style={{fontFamily:DS.fontDisplay,fontSize:13,fontWeight:700,color:DS.navy}}>Charges variables</div>
            <div style={{fontSize:11,color:"#888",marginTop:1}}>Total : {fmtE(totalChargesVar)}</div>
          </div>
          <button onClick={() => setChargeModal("new")} style={{background:DS.navy,color:"#fff",border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}>+ Charge</button>
        </div>
        {chargesVar.length === 0 && <div style={{fontSize:11,color:"#ccc"}}>Aucune charge variable ce mois</div>}
        {chargesVar.map(c => (
          <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #eef2f7"}}>
            <span style={{fontSize:12,color:"#444"}}>{c.label}</span>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontFamily:DS.fontDisplay,fontSize:13,fontWeight:700,color:DS.charge}}>{fmtE(c.montant)}</span>
              <button onClick={() => setChargeModal(c)} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}}>✏️</button>
              <button onClick={() => setConfirmDel({ type: "charge", id: c.id })} style={{background:"none",border:"none",cursor:"pointer",fontSize:13}}>🗑️</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
